import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isClient } from '../../../utilities/functions';
import { Button, Icon, List, classNames } from '@pella/sash-framework';
import { isValidArray } from '../../../utilities/validations';
import Image from '../../image';
import { graphql, useStaticQuery } from 'gatsby';
import { cdnQuery } from '../../../utilities/cdnQuery';

const FifteenMinuteApptExitIntent = ({ branchID, update15MinParent, view }) => {
	const { professional } = useStaticQuery(query).contentstackConversionConfig;
	const { alternate_headings, learn_about } = professional;

	const salesPersonCardHeading = alternate_headings?.find((heading) => heading.heading_type === view).heading;
	const salesPersonCardSubheading = alternate_headings?.find((heading) => heading.heading_type === view)?.subheading;

	const [salesBio, setSalesBio] = useState({});
	const [exitIntentState, setExitIntentState] = useState('default');
	const [exitIntentPosition, setExitIntentPosition] = useState('absolute');

	useEffect(() => {
		fetchBioFromContentstack(branchID), [branchID];
	}, [branchID]);

	const fetchBioFromContentstack = (branchID) => {
		const query = `{
			"inhome": { "$eq": true },
			"$or": [
				{
					"branch_data": {
						"$in_query": {
							"branch_retail": {
								"$in": ${JSON.stringify([branchID])}
							}
						}
					}
				},
				{
					"branch_data": {
						"$in_query": {
							"branch_trade": {
								"$in": ${JSON.stringify([branchID])}
							}
						}
					}
				}
			]
		}`;

		cdnQuery('author', query, 'include[]=author_bio&include[]=image')
			.then((response) => setSalesBio(response.data.entries[0]))
			.catch(() => {});
	};

	if (isClient) {
		const footer = document.getElementById('footer');
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						setExitIntentPosition('absolute');
					} else {
						setExitIntentPosition('fixed');
					}
				});
			},
			{
				root: null,
				threshold: 0,
			}
		);
		observer?.observe(footer);
	}

	const handleButtonClick = () => {
		const appointmentComponent = document.getElementById('15MinAppt-rta');

		if (appointmentComponent) {
			appointmentComponent.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}

		update15MinParent(true);

		setExitIntentState('hidden');
	};

	const fifteenMinuteAppointmentClasses = classNames({
		'm-3 max-w-[336px] max-md:w-full border border-neutral-400 bg-white bottom-0 right-0 border border-neutral-200 shadow-sm z-30': true,
		'p-5': exitIntentState === 'default',
		'md:max-w-[720px]': exitIntentState !== 'default',
		'absolute': exitIntentPosition === 'absolute',
		'fixed': exitIntentPosition === 'fixed',
	});

	if (exitIntentState === 'hidden') return;

	return (
		<div
			id='15MinAppt-exit_intent'
			className={fifteenMinuteAppointmentClasses}
		>
			<Button
				className='absolute right-3 top-3 !w-fit !p-0'
				color='tertiary-none'
				onClick={() => setExitIntentState('hidden')}
			>
				<span className='!m-0 text-3xl font-normal leading-6'>×</span>
				<span className='sr-only'>Close</span>
			</Button>

			{exitIntentState === 'default' && (
				<>
					<div
						id='salesPersonCard'
						className='flex flex-col items-start md:gap-x-8'
					>
						<h2 className='text-subtitle m-0 mb-3 font-bold'>{salesPersonCardHeading}</h2>
						<p className='text-body-2'>{salesPersonCardSubheading}</p>

						<div
							id='salesPersonCard-bio'
							className='my-8 flex flex-row items-center md:row-span-3 md:row-start-1'
						>
							<Image
								data={salesBio?.image?.[0]}
								params='?format=pjpg&auto=webp&quality=90'
								className='mr-5 grid h-16 w-16 place-items-center rounded-full bg-neutral-200 md:mr-4'
							/>

							<div>
								<p className='text-subtitle-2 font-semibold'>{salesBio?.in_home_bio?.author_name}</p>
								<div
									className='text-body-2 mt-0.5 text-black'
									dangerouslySetInnerHTML={{ __html: salesBio?.in_home_bio?.job_title }}
								/>
							</div>
						</div>

						<div
							id='salesPersonCard-learn'
							className='w-full bg-neutral-50 px-3 py-5'
						>
							<p className='text-tiny uppercase'>{learn_about.heading}</p>
							{isValidArray(learn_about.group) && (
								<List
									bullets='none'
									className='flex flex-col'
								>
									{learn_about.group.map((item, index) => (
										<li
											key={index}
											className='mt-4 flex items-center'
										>
											<Icon
												name={item.material_symbol}
												size={20}
												weight={700}
												className='mr-3 text-neutral-600'
											/>
											<p className='text-body-2 text-black'>{item.icon_heading}</p>
										</li>
									))}
								</List>
							)}
						</div>
					</div>

					<Button
						color='primary-alt'
						onClick={handleButtonClick}
						className='mt-5 flex !w-full items-center'
					>
						<Icon
							size={20}
							name='calendar_month'
						/>

						<span>Schedule A 15 Min Call</span>
					</Button>
				</>
			)}
		</div>
	);
};

FifteenMinuteApptExitIntent.propTypes = {
	branchID: PropTypes.string,
	update15MinParent: PropTypes.func,
	view: PropTypes.string,
};

export default FifteenMinuteApptExitIntent;

export const query = graphql`
	query {
		contentstackConversionConfig {
			professional {
				alternate_headings {
					heading
					heading_type
					subheading
				}
				learn_about {
					heading
					group {
						icon_heading
						material_symbol
					}
				}
			}
		}
	}
`;
